/**
 * Geographic utility functions — centroid lookups, bbox calculations, location indexing
 */

// Common country name aliases → ISO_A3
const COUNTRY_ALIASES = {
  usa: 'USA',
  us: 'USA',
  'united states': 'USA',
  america: 'USA',
  'united states of america': 'USA',
  uk: 'GBR',
  'united kingdom': 'GBR',
  britain: 'GBR',
  'great britain': 'GBR',
  england: 'GBR',
  holland: 'NLD',
  netherlands: 'NLD',
  'the netherlands': 'NLD',
  russia: 'RUS',
  'russian federation': 'RUS',
  'south korea': 'KOR',
  korea: 'KOR',
  'north korea': 'PRK',
  iran: 'IRN',
  syria: 'SYR',
  'czech republic': 'CZE',
  czechia: 'CZE',
  'ivory coast': 'CIV',
  "cote d'ivoire": 'CIV',
  congo: 'COD',
  'dr congo': 'COD',
  drc: 'COD',
  'republic of the congo': 'COG',
  burma: 'MMR',
  myanmar: 'MMR',
  uae: 'ARE',
  'united arab emirates': 'ARE',
  saudi: 'SAU',
  'saudi arabia': 'SAU',
  taiwan: 'TWN',
  vietnam: 'VNM',
  'viet nam': 'VNM',
}

// Continent and subregion bounding boxes for flyTo
const CONTINENT_VIEWS = {
  Africa: { longitude: 20, latitude: 0, zoom: 2.8 },
  Asia: { longitude: 90, latitude: 30, zoom: 2.5 },
  Europe: { longitude: 15, latitude: 50, zoom: 3.5 },
  'North America': { longitude: -98.5, latitude: 39.5, zoom: 2.5 },
  'South America': { longitude: -60, latitude: -15, zoom: 2.8 },
  Oceania: { longitude: 140, latitude: -25, zoom: 3 },
  Antarctica: { longitude: 0, latitude: -80, zoom: 2.5 },
  'Central America': { longitude: -85, latitude: 13, zoom: 4.5 },
  Caribbean: { longitude: -72, latitude: 18, zoom: 5 },
  'Middle East': { longitude: 45, latitude: 28, zoom: 3.8 },
  'Southeast Asia': { longitude: 110, latitude: 5, zoom: 3.5 },
  'Western Europe': { longitude: 5, latitude: 48, zoom: 4 },
  'Eastern Europe': { longitude: 22, latitude: 48, zoom: 4 },
  Scandinavia: { longitude: 15, latitude: 63, zoom: 3.8 },
  'East Asia': { longitude: 115, latitude: 35, zoom: 3.2 },
  'South Asia': { longitude: 78, latitude: 22, zoom: 3.5 },
  'Southern Africa': { longitude: 28, latitude: -22, zoom: 4 },
  'West Africa': { longitude: -5, latitude: 10, zoom: 4 },
  'East Africa': { longitude: 37, latitude: 2, zoom: 4 },
  'North Africa': { longitude: 15, latitude: 28, zoom: 3.8 },
  'Pacific Islands': { longitude: 170, latitude: -10, zoom: 3.5 },
}

// Camera overrides for countries whose geometric centroid is misleading
// (non-contiguous territories, extreme outlier landmasses, overseas departments)
const CAMERA_OVERRIDES = {
  USA: { centroid: [-98.5, 39.5], zoom: 3.2 }, // Contiguous US center (Alaska skews to Arctic)
  RUS: { centroid: [55, 60], zoom: 2.5 }, // European/Ural Russia (Siberia skews east)
  FRA: { centroid: [2.2, 46.2], zoom: 4.5 }, // Metropolitan France (French Guiana skews west)
  NOR: { centroid: [10, 62], zoom: 3.8 }, // Mainland Norway (Svalbard skews north)
  DNK: { centroid: [10, 56], zoom: 5.5 }, // Denmark proper (Greenland skews northwest)
  NLD: { centroid: [5.3, 52.1], zoom: 6 }, // Netherlands proper (Caribbean islands skew)
  GBR: { centroid: [-2, 54], zoom: 4.8 }, // Great Britain (overseas territories skew)
  NZL: { centroid: [173, -41], zoom: 4.5 }, // New Zealand (Chatham Islands etc.)
  CHL: { centroid: [-71, -35], zoom: 3.5 }, // Chile proper (Easter Island skews west)
  PRT: { centroid: [-8.5, 39.5], zoom: 5.5 }, // Portugal proper (Azores/Madeira skew)
  ECU: { centroid: [-78.5, -1.5], zoom: 5.5 }, // Ecuador proper (Galápagos skews west)
}

// Regions defined by explicit ISO-3 codes (avoids UN subregion/continent classification issues)
const SUBREGION_COUNTRIES = {
  'Central America': new Set(['GTM', 'BLZ', 'HND', 'SLV', 'NIC', 'CRI', 'PAN']),
  'North America': new Set(['USA', 'CAN', 'MEX']),
  Caribbean: new Set([
    'CUB',
    'JAM',
    'HTI',
    'DOM',
    'TTO',
    'BHS',
    'BRB',
    'ATG',
    'DMA',
    'GRD',
    'KNA',
    'LCA',
    'VCT',
    'PRI',
    'ABW',
    'CUW',
    'SXM',
  ]),
  'Middle East': new Set([
    'SAU',
    'IRN',
    'IRQ',
    'ISR',
    'JOR',
    'LBN',
    'SYR',
    'ARE',
    'KWT',
    'QAT',
    'BHR',
    'OMN',
    'YEM',
    'PSE',
    'TUR',
    'CYP',
    'EGY',
  ]),
  'Southeast Asia': new Set([
    'THA',
    'VNM',
    'MYS',
    'IDN',
    'PHL',
    'MMR',
    'KHM',
    'LAO',
    'SGP',
    'BRN',
    'TLS',
  ]),
  'Western Europe': new Set([
    'FRA',
    'DEU',
    'GBR',
    'ITA',
    'ESP',
    'PRT',
    'NLD',
    'BEL',
    'LUX',
    'IRL',
    'AUT',
    'CHE',
  ]),
  'Eastern Europe': new Set([
    'POL',
    'CZE',
    'SVK',
    'HUN',
    'ROU',
    'BGR',
    'UKR',
    'BLR',
    'MDA',
    'SRB',
    'HRV',
    'SVN',
    'BIH',
    'MNE',
    'MKD',
    'ALB',
    'KOS',
  ]),
  Scandinavia: new Set(['NOR', 'SWE', 'DNK', 'FIN', 'ISL']),
  'East Asia': new Set(['CHN', 'JPN', 'KOR', 'PRK', 'MNG', 'TWN']),
  'South Asia': new Set(['IND', 'PAK', 'BGD', 'LKA', 'NPL', 'BTN', 'MDV', 'AFG']),
  'Southern Africa': new Set([
    'ZAF',
    'BWA',
    'NAM',
    'ZMB',
    'ZWE',
    'MOZ',
    'MWI',
    'LSO',
    'SWZ',
    'MDG',
  ]),
  'West Africa': new Set([
    'NGA',
    'GHA',
    'CIV',
    'SEN',
    'MLI',
    'BFA',
    'NER',
    'GIN',
    'SLE',
    'LBR',
    'TGO',
    'BEN',
    'GMB',
    'GNB',
    'CPV',
    'MRT',
  ]),
  'East Africa': new Set(['KEN', 'TZA', 'UGA', 'ETH', 'SOM', 'ERI', 'DJI', 'RWA', 'BDI', 'SSD']),
  'North Africa': new Set(['MAR', 'DZA', 'TUN', 'LBY', 'EGY', 'SDN']),
  'Pacific Islands': new Set([
    'FJI',
    'PNG',
    'SLB',
    'VUT',
    'WSM',
    'TON',
    'KIR',
    'FSM',
    'MHL',
    'PLW',
    'TUV',
    'NRU',
  ]),
  'Central Asia': new Set(['KAZ', 'UZB', 'TKM', 'KGZ', 'TJK']),
  Balkans: new Set(['ALB', 'BIH', 'BGR', 'HRV', 'GRC', 'KOS', 'MNE', 'MKD', 'ROU', 'SRB', 'SVN']),
  // Sub-Saharan Africa = West + East + Southern + Central Africa + Indian Ocean
  // islands; excludes North Africa (MAR/DZA/TUN/LBY/EGY/SDN).
  'Sub-Saharan Africa': new Set([
    // West
    'NGA',
    'GHA',
    'CIV',
    'SEN',
    'MLI',
    'BFA',
    'NER',
    'GIN',
    'SLE',
    'LBR',
    'TGO',
    'BEN',
    'GMB',
    'GNB',
    'CPV',
    'MRT',
    // East
    'KEN',
    'TZA',
    'UGA',
    'ETH',
    'SOM',
    'ERI',
    'DJI',
    'RWA',
    'BDI',
    'SSD',
    // Central
    'CMR',
    'CAF',
    'TCD',
    'COG',
    'COD',
    'GAB',
    'GNQ',
    'STP',
    'AGO',
    // Southern
    'ZAF',
    'BWA',
    'NAM',
    'ZMB',
    'ZWE',
    'MOZ',
    'MWI',
    'LSO',
    'SWZ',
    'MDG',
    // Indian Ocean
    'COM',
    'SYC',
    'MUS',
  ]),
}

/**
 * Compute centroid from a GeoJSON geometry
 */
export function computeCentroid(geometry) {
  const coords = extractAllCoords(geometry)
  if (coords.length === 0) return [0, 0]

  let sumLng = 0
  let sumLat = 0
  for (const [lng, lat] of coords) {
    sumLng += lng
    sumLat += lat
  }
  return [sumLng / coords.length, sumLat / coords.length]
}

/**
 * Compute bounding box from a GeoJSON geometry
 * Returns [minLng, minLat, maxLng, maxLat]
 */
export function computeBbox(geometry) {
  const coords = extractAllCoords(geometry)
  if (coords.length === 0) return [-180, -90, 180, 90]

  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng
    if (lat < minLat) minLat = lat
    if (lng > maxLng) maxLng = lng
    if (lat > maxLat) maxLat = lat
  }
  return [minLng, minLat, maxLng, maxLat]
}

/**
 * Calculate zoom level from bounding box
 */
export function zoomFromBbox(bbox) {
  const [minLng, minLat, maxLng, maxLat] = bbox
  const lngSpan = maxLng - minLng
  const latSpan = maxLat - minLat
  const maxSpan = Math.max(lngSpan, latSpan)

  if (maxSpan > 180) return 1.2
  if (maxSpan > 90) return 2
  if (maxSpan > 45) return 3
  if (maxSpan > 20) return 4
  if (maxSpan > 10) return 5
  if (maxSpan > 5) return 6
  if (maxSpan > 2) return 7
  if (maxSpan > 1) return 8
  return 9
}

/**
 * Recursively extract all coordinate pairs from a GeoJSON geometry
 */
function extractAllCoords(geometry) {
  if (!geometry) return []

  if (geometry.type === 'Point') {
    return [geometry.coordinates]
  }
  if (geometry.type === 'MultiPoint' || geometry.type === 'LineString') {
    return geometry.coordinates
  }
  if (geometry.type === 'MultiLineString' || geometry.type === 'Polygon') {
    return geometry.coordinates.flat()
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.flat(2)
  }
  if (geometry.type === 'GeometryCollection') {
    return geometry.geometries.flatMap((g) => extractAllCoords(g))
  }
  return []
}

/**
 * Build a location index from loaded GeoJSON data.
 * Uses separate maps per dataset so lookup can enforce priority:
 * cities first, then countries, then states/provinces.
 */
export function buildLocationIndex(countriesData, statesData, citiesData) {
  const cities = new Map()
  const countries = new Map()
  const states = new Map()
  const continents = new Map()

  // Index cities (highest priority for name lookups)
  // Handle both 110m (uppercase props: NAME, LONGITUDE) and 50m (lowercase: name, longitude)
  if (citiesData?.features) {
    for (const feature of citiesData.features) {
      const props = feature.properties
      const name = props.NAME || props.name
      const lng =
        props.LONGITUDE || props.longitude || (feature.geometry && feature.geometry.coordinates[0])
      const lat =
        props.LATITUDE || props.latitude || (feature.geometry && feature.geometry.coordinates[1])
      const pop = props.POP_MAX || props.pop_max || 0
      const country = props.ADM0NAME || props.adm0name

      if (name && lng != null && lat != null) {
        const key = name.toLowerCase()
        const entry = {
          centroid: [lng, lat],
          bbox: [lng - 0.5, lat - 0.5, lng + 0.5, lat + 0.5],
          zoom: 8,
          dataset: 'cities',
          feature,
          name,
          country,
          population: pop,
        }

        // For duplicate city names (e.g., Sydney CA vs Sydney AU), keep the larger one
        const existing = cities.get(key)
        if (!existing || pop > (existing.population || 0)) {
          cities.set(key, entry)
        }

        // Always index the qualified "city, country" form
        if (country) {
          cities.set(`${key}, ${country.toLowerCase()}`, entry)
        }
      }
    }
  }

  // Index countries
  if (countriesData?.features) {
    for (const feature of countriesData.features) {
      const props = feature.properties
      const name = props.NAME || props.name
      // CL-261: Natural Earth sets ISO_A3 = '-99' for France, Norway, Kosovo…,
      // which silently skipped their CAMERA_OVERRIDES ("Go to France" framed
      // on the mid-Atlantic, pulled by French Guiana) and their ISO lookups.
      const iso3 = [props.ISO_A3, props.ISO_A3_EH, props.ADM0_A3].find((v) => v && v !== '-99')
      const iso2 = [props.ISO_A2, props.ISO_A2_EH].find((v) => v && v !== '-99')
      const override = iso3 ? CAMERA_OVERRIDES[iso3] : null
      const centroid = override?.centroid || computeCentroid(feature.geometry)
      const bbox = computeBbox(feature.geometry)
      const zoom = override?.zoom || Math.min(zoomFromBbox(bbox), 6)

      const entry = { centroid, bbox, zoom, dataset: 'countries', feature, name, iso3 }

      if (name) countries.set(name.toLowerCase(), entry)
      if (iso3 && iso3 !== '-99') countries.set(iso3.toLowerCase(), entry)
      if (iso2 && iso2 !== '-99') countries.set(iso2.toLowerCase(), entry)
    }
  }

  // Add country aliases
  for (const [alias, iso3] of Object.entries(COUNTRY_ALIASES)) {
    const existing = countries.get(iso3.toLowerCase())
    if (existing) {
      countries.set(alias, existing)
    }
  }

  // Index states/provinces
  if (statesData?.features) {
    for (const feature of statesData.features) {
      const props = feature.properties
      const name = props.name
      const admin = props.admin
      const centroid = computeCentroid(feature.geometry)
      const bbox = computeBbox(feature.geometry)
      const zoom = zoomFromBbox(bbox)

      const entry = {
        centroid,
        bbox,
        zoom: Math.min(zoom, 7),
        dataset: 'states_provinces',
        feature,
        name,
        admin,
      }

      if (name) {
        states.set(name.toLowerCase(), entry)
        if (admin) {
          states.set(`${name.toLowerCase()}, ${admin.toLowerCase()}`, entry)
        }
      }
    }
  }

  // Add continent entries
  for (const [name, view] of Object.entries(CONTINENT_VIEWS)) {
    continents.set(name.toLowerCase(), {
      centroid: [view.longitude, view.latitude],
      bbox: null,
      zoom: view.zoom,
      dataset: 'continent',
      name,
    })
  }

  return { cities, countries, states, continents }
}

/**
 * Find a location in the index.
 * Priority: exact city → exact country → exact state → exact continent
 *           → then "key starts with query" or "query starts with key" (but NOT arbitrary substring)
 * Returns { ...entry, matchType: 'exact' | 'fuzzy' }
 */
export function findLocation(index, query) {
  if (!query) return null
  let q = query.toLowerCase().trim()

  // Strip common prefixes
  q = q.replace(/^the\s+/, '')

  const { cities, countries, states, continents } = index

  // --- Pass 1: Exact match in priority order ---
  if (cities.has(q)) return { ...cities.get(q), matchType: 'exact' }
  if (countries.has(q)) return { ...countries.get(q), matchType: 'exact' }
  if (states.has(q)) return { ...states.get(q), matchType: 'exact' }
  if (continents.has(q)) return { ...continents.get(q), matchType: 'exact' }

  // --- Pass 2: Safe fuzzy — only "key starts with query" or "query starts with key" ---
  //     Minimum key length of 4 to avoid matching 2-3 letter ISO codes as substrings.
  const allMaps = [
    cities, // check cities first
    countries,
    states,
    continents,
  ]

  for (const map of allMaps) {
    for (const [key, value] of map) {
      if (key.length < 4) continue // skip short keys (ISO codes) in fuzzy pass
      if (key.startsWith(q) || q.startsWith(key)) {
        return { ...value, matchType: 'fuzzy' }
      }
    }
  }

  return null
}

/**
 * Format a number for display (e.g., 1234567 → "1.2M")
 */
export function formatNumber(n) {
  if (n == null || isNaN(n)) return 'N/A'
  if (Math.abs(n) >= 1e9) return (n / 1e9).toFixed(1) + 'B'
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + 'M'
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'K'
  return n.toLocaleString()
}

/**
 * Point-in-polygon test using the ray-casting algorithm.
 * @param {number} lng - Longitude of the test point
 * @param {number} lat - Latitude of the test point
 * @param {number[][][]} polygon - Polygon coordinate rings (outer ring + optional holes)
 * @returns {boolean} True if the point is inside the polygon
 */
export function pointInPolygon(lng, lat, polygon) {
  let inside = false
  const ring = polygon[0] // outer ring
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0]
    const yi = ring[i][1]
    const xj = ring[j][0]
    const yj = ring[j][1]
    const intersect = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

/**
 * Check if a point falls inside any feature in a GeoJSON FeatureCollection.
 * Handles Polygon and MultiPolygon geometries.
 * @param {number} lng - Longitude
 * @param {number} lat - Latitude
 * @param {object} geojson - GeoJSON FeatureCollection
 * @returns {boolean}
 */
export function pointInFeatureCollection(lng, lat, geojson) {
  for (const feature of geojson.features) {
    const geom = feature.geometry
    if (geom.type === 'Polygon') {
      if (pointInPolygon(lng, lat, geom.coordinates)) return true
    } else if (geom.type === 'MultiPolygon') {
      for (const polygon of geom.coordinates) {
        if (pointInPolygon(lng, lat, polygon)) return true
      }
    }
  }
  return false
}

// CL-261: continent / region tokens the parser emits in the `country` slot
// ("show me the countries of Africa" → country 'AFR'). None has a boundary
// file, so show_country/show_divisions fetched AFR_ADM1 → null → blank globe.
// Only non-ISO codes here: NAM is Namibia, AUS Australia, so those never map.
const REGION_CODES = { AFR: 'Africa', EUR: 'Europe', ASI: 'Asia', OCE: 'Oceania' }
const REGION_DEMONYMS = {
  african: 'Africa',
  european: 'Europe',
  asian: 'Asia',
  oceanian: 'Oceania',
  'south american': 'South America',
  'north american': 'North America',
}
const REGION_NAMES = new Map(
  [...Object.keys(CONTINENT_VIEWS), ...Object.keys(SUBREGION_COUNTRIES)].map((r) => [
    r.toLowerCase(),
    r,
  ])
)

/**
 * Map a parser `country` value to a continent / named region, or null when it
 * is (or may be) a real country.
 * @param {string} token
 * @returns {string|null} canonical region name (a CONTINENT_VIEWS / SUBREGION_COUNTRIES key)
 */
export function resolveRegionToken(token) {
  if (!token || typeof token !== 'string') return null
  const t = token.trim()
  if (REGION_CODES[t.toUpperCase()]) return REGION_CODES[t.toUpperCase()]
  const lower = t
    .toLowerCase()
    .replace(/^the\s+/, '')
    .replace(/\s+(?:countries|nations|continent|region)$/, '')
  return REGION_NAMES.get(lower) || REGION_DEMONYMS[lower] || null
}

export { CONTINENT_VIEWS, COUNTRY_ALIASES, SUBREGION_COUNTRIES }
